// Sistema de keep-alive para manter o site ativo no Render
// Funciona especialmente bem quando instalado como PWA (APK do PWABuilder)
// Mantém o servidor ativo mesmo quando o app está em segundo plano

import { getApiEndpoint } from './api-url';

let keepAliveInterval: NodeJS.Timeout | null = null;
let isActive = false;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

/**
 * Detectar se o app está instalado como PWA
 */
const isPWAInstalled = (): boolean => {
  // Verificar se está em modo standalone (PWA instalado)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Verificar se está rodando como app instalado (navigator.standalone para iOS)
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  
  // Verificar se foi aberto a partir da tela inicial (referrer)
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

/**
 * Detectar se a página está em background
 */
const isPageVisible = (): boolean => {
  return !document.hidden;
};

/**
 * Fazer requisição keep-alive para o servidor com retry automático
 */
const performKeepAlive = async (retryCount: number = 0): Promise<void> => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 5000; // 5 segundos entre tentativas
  
  try {
    // Usar URL absoluta do backend (Render)
    const healthUrl = getApiEndpoint('/health');
    
    const response = await fetch(healthUrl, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Keep-alive OK:', data);
    } else {
      console.warn('⚠️ Keep-alive falhou:', response.status);
      
      // Retry se falhou e ainda temos tentativas
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Tentando novamente (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return performKeepAlive(retryCount + 1);
      }
    }
  } catch (error) {
    console.error('❌ Erro no keep-alive:', error);
    
    // Retry se erro de rede e ainda temos tentativas
    if (retryCount < MAX_RETRIES) {
      console.log(`🔄 Tentando novamente após erro (${retryCount + 1}/${MAX_RETRIES})...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return performKeepAlive(retryCount + 1);
    }
  }
};

/**
 * Registrar Background Sync no Service Worker
 */
const registerBackgroundSync = async (): Promise<void> => {
  if (!serviceWorkerRegistration) {
    return;
  }
  
  try {
    // Verificar suporte a Background Sync
    if ('sync' in serviceWorkerRegistration) {
      // Registrar sync tag para keep-alive
      await (serviceWorkerRegistration as any).sync.register('keep-alive');
      console.log('✅ Background Sync registrado para keep-alive');
    } else {
      console.warn('⚠️ Background Sync não suportado neste navegador');
    }
  } catch (error) {
    console.error('❌ Erro ao registrar Background Sync:', error);
  }
};

/**
 * Inicializar sistema de keep-alive
 * Funciona especialmente bem quando instalado como PWA (APK)
 */
export const initializeKeepAlive = async (): Promise<boolean> => {
  if (isActive) {
    console.log('🔄 Keep-alive já está ativo');
    return true;
  }
  
  console.log('🔄 Inicializando sistema de keep-alive...');
  console.log('📱 PWA instalado:', isPWAInstalled());
  
  // Registrar Service Worker se disponível
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      serviceWorkerRegistration = registration;
      console.log('✅ Service Worker pronto para keep-alive');
      
      // Registrar Background Sync
      await registerBackgroundSync();
    } catch (error) {
      console.warn('⚠️ Service Worker não disponível:', error);
    }
  }
  
  // Configurar polling periódico - REDUZIDO para prevenir hibernação do Render
  // Render hiberna após ~15 minutos, então precisamos pingar a cada 2 minutos
  const getInterval = (): number => {
    // Sempre usar 2 minutos (mais frequente) para garantir que o servidor não hiberne
    // Mesmo em background, manter keep-alive ativo
    return 2 * 60 * 1000; // 2 minutos sempre (mais seguro)
  };
  
  // Função para executar keep-alive
  const executeKeepAlive = async (): Promise<void> => {
    const interval = getInterval();
    
    // Sempre executar keep-alive (intervalo nunca é 0 agora)
    await performKeepAlive();
    
    // Agendar próximo keep-alive
    if (keepAliveInterval) {
      clearInterval(keepAliveInterval);
    }
    
    keepAliveInterval = setTimeout(() => {
      executeKeepAlive();
    }, interval);
  };
  
  // Executar primeiro keep-alive imediatamente
  await executeKeepAlive();
  
  // Escutar mudanças de visibilidade da página
  document.addEventListener('visibilitychange', () => {
    if (isPageVisible()) {
      console.log('👁️ Página visível - continuando keep-alive');
      executeKeepAlive();
    } else {
      console.log('👁️‍🗨️ Página em background - mantendo keep-alive ativo');
      // SEMPRE manter keep-alive ativo, mesmo em background
      // Isso é crítico para prevenir hibernação do Render
      executeKeepAlive();
    }
  });
  
  // Escutar quando a página volta ao foco (importante para PWA)
  window.addEventListener('focus', () => {
    console.log('🎯 Página em foco - executando keep-alive');
    executeKeepAlive();
  });
  
  isActive = true;
  console.log('✅ Sistema de keep-alive inicializado');
  
  return true;
};

/**
 * Parar sistema de keep-alive
 */
export const stopKeepAlive = (): void => {
  if (!isActive) {
    return;
  }
  
  if (keepAliveInterval) {
    clearInterval(keepAliveInterval);
    keepAliveInterval = null;
  }
  
  isActive = false;
  console.log('🛑 Sistema de keep-alive parado');
};

/**
 * Verificar status do sistema de keep-alive
 */
export const getKeepAliveStatus = () => {
  return {
    isActive,
    isPWAInstalled: isPWAInstalled(),
    isPageVisible: isPageVisible(),
    hasServiceWorker: !!serviceWorkerRegistration,
    interval: keepAliveInterval ? 'ativo' : 'inativo',
  };
};

